// index.tsx
import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import { ThemeProvider as EmotionThemeProvider } from "@emotion/react";
import emotionTheme from "./theme/components/theme";
import { ThemeProvider as CustomThemeProvider } from "./theme/components/ThemeProvider";
import RootRouter from "./routes";
import "./index.css";
import "./theme/styles/themes.css";
import "reactflow/dist/style.css";

import { Provider } from "react-redux";
import { store, persistor } from "./shared/redux/store/store";
import { PersistGate } from "redux-persist/integration/react";

import axios from "axios";

import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";

import {
  setUserProfile,
  setUserProfileError,
  setUserProfileLoading,
} from "./shared/redux/slices/userProfileSlice";
import { useAppDispatch, useAppSelector } from "./shared/redux/hooks";
import { getDisplayName } from "@shared/components/utils/displayNameHelper";
import ResponsiveGate from "@shared/components/ResponsiveGate";
import { clearSelection, setTenant } from "@redux/slices/tenantSlice";
import Auth0RedirectHandler from "@shared/components/Auth0RedirectHandler";
import UserEditorHost from "@shared/components/UserEditorHost";
import ChunkReloadPrompt from "@shared/components/ChunkReloadPrompt";
import { setTenantNotice, setLoading } from "./shared/redux/slices/tenantNoticesSlice";
import {
  setMaintenance,
  maintenanceCheckFailed,
  setMaintenanceLoading,
} from "./shared/redux/slices/maintenanceSlice";

import { getAuthRedirectUri } from "./shared/config/authRedirect";

const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN || "";
const auth0ClientId = process.env.REACT_APP_AUTH0_CLIENT_ID || "";
const auth0Audience = process.env.REACT_APP_AUTH0_AUDIENCE || "";
const auth0RedirectUri = getAuthRedirectUri();

// Base de la API y /v1
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3002";
const API_V1_URL = `${API_BASE_URL}/v1`;

// Helper function to check if we should load react-grab
// Load in development and staging, NOT in production
const shouldLoadReactGrab = (): boolean => {
  const nodeEnv = process.env.NODE_ENV || '';
  const appEnv = (process.env.REACT_APP_ENV || '').toLowerCase();
  
  // Always load in local development
  if (nodeEnv === 'development') {
    return true;
  }
  
  // Load in staging, don't load in production
  return appEnv === 'staging';
};

// Conditionally load React Grab - show in development and staging, hide in production
const shouldLoad = shouldLoadReactGrab();

// Log environment for debugging (visible in both dev and prod)
console.log('[Environment] NODE_ENV:', process.env.NODE_ENV, 'REACT_APP_ENV:', process.env.REACT_APP_ENV);

if (shouldLoad) {
  const script = document.createElement('script');
  script.src = '//unpkg.com/react-grab/dist/index.global.js';
  script.crossOrigin = 'anonymous';
  script.setAttribute('data-enabled', 'true');
  document.head.appendChild(script);
}

const container = document.getElementById("root");
if (!container) {
  throw new Error("Couldn't find the #root element in the HTML");
}
const root = ReactDOM.createRoot(container);

async function fetchUserProfile(token: string) {
  try {
    const response = await axios.post(
      `${API_V1_URL}/auth0/user-profile`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error al llamar /auth0/user-profile:", error);
    throw error;
  }
}

async function fetchTenantNotices(token: string, tenantId: number) {
  try {
    const response = await axios.get(
      `${API_V1_URL}/tenant-notices/?tenantId=${tenantId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    console.log(response.data, 'response notice')
    return response.data;
  } catch (error) {
    console.error("Error al llamar /tenant-notices:", error);
    return null;
  }
}


async function fetchMaintenanceStatus(token: string) {
  const response = await axios.get(`${API_V1_URL}/maintenance`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
}

/**
 * Polls the maintenance status (CTX-483).
 *
 * Polling rather than a one-shot fetch on purpose: a window is announced
 * while people already have the dashboard open, and the whole point of the
 * notice is that they see it without being told to reload. The same poll is
 * what takes the notice away — and puts the live status back — when the
 * window closes.
 */
const MAINTENANCE_POLL_MS = 60000;

function LoadMaintenanceStatus() {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;

    const check = async () => {
      try {
        const token = await getAccessTokenSilently();
        const payload = await fetchMaintenanceStatus(token);
        if (cancelled) return;
        dispatch(
          setMaintenance({
            byProduct: payload?.data?.byProduct ?? {},
            upcomingByProduct: payload?.data?.upcomingByProduct ?? {},
          })
        );
      } catch (err) {
        if (cancelled) return;
        // Leave whatever we already knew in place: a failed poll says
        // nothing about whether the window is still open, and clearing it
        // would drop the notice in the middle of the maintenance.
        console.error("Error fetching maintenance status:", err);
        dispatch(maintenanceCheckFailed());
      }
    };

    dispatch(setMaintenanceLoading(true));
    check();
    const timer = setInterval(check, MAINTENANCE_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [isAuthenticated, getAccessTokenSilently, dispatch]);

  return null;
}

function LoadUserProfileOnMount() {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const dispatch = useAppDispatch();
  const [hasFetchedProfile, setHasFetchedProfile] = React.useState(false);

  useEffect(() => {
    if (!isLoading && isAuthenticated && !hasFetchedProfile) {
      setHasFetchedProfile(true);
      dispatch(setUserProfileLoading());
      (async () => {
        try {
          const token = await getAccessTokenSilently();
          const data = await fetchUserProfile(token);
          dispatch(setUserProfile(data));
          //           
          if (data.TenantType === "CUSTOMER") {
            //nuke any old partner/tenant
            dispatch(clearSelection());
            dispatch(setTenant({
              id:   data.tenantId,
              name: data.Tenant.nameDisplay,
              domain: "" 
            }));
          }
        } catch (err) {
          console.error("Error al obtener token y/o user-profile:", err);
          // Surfaces are waiting on this: without it they would keep showing a
          // loading state for a profile that is never going to arrive.
          dispatch(setUserProfileError());
        } finally {
        //Added to dynamically render the name of the host in the tab
        document.title =  `${getDisplayName()}`;
        }
      })();
    }
  }, [isLoading, isAuthenticated, hasFetchedProfile, getAccessTokenSilently, dispatch]);

  return null;
}

function LoadTenantNotices() {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const dispatch = useAppDispatch();
  const tenantId = useAppSelector((state) => state.selection.tenantId);
  const [lastFetchedTenantId, setLastFetchedTenantId] = React.useState<number | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !tenantId || tenantId === lastFetchedTenantId) {
      return;
    }
    // Dispatch loading state everytime tenantId changes
    dispatch(setLoading(true));
    setLastFetchedTenantId(tenantId);

    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const noticesData = await fetchTenantNotices(token, tenantId);
        
        if (noticesData?.data && noticesData.data.active) {
          dispatch(setTenantNotice(noticesData.data));
        } else {
          dispatch(setTenantNotice(null));
        }
      } catch (err) {
        console.error("Error al obtener tenant notices:", err);
        dispatch(setTenantNotice(null));
      }
    })();
  }, [isAuthenticated, tenantId, lastFetchedTenantId, getAccessTokenSilently, dispatch]);
  return null;
}

root.render(
  <EmotionThemeProvider theme={emotionTheme}>
    <CustomThemeProvider>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <Auth0Provider
            domain={auth0Domain}
            clientId={auth0ClientId}
            authorizationParams={{
              audience: auth0Audience,
              redirect_uri: auth0RedirectUri,
            }}
            cacheLocation="localstorage"
          >
            <ResponsiveGate>
              <RootRouter />
              <Auth0RedirectHandler />
              <LoadUserProfileOnMount />
              <LoadTenantNotices />
              <LoadMaintenanceStatus />
              <UserEditorHost />
              <ChunkReloadPrompt />
            </ResponsiveGate>
          </Auth0Provider>
        </PersistGate>
      </Provider>
    </CustomThemeProvider>
  </EmotionThemeProvider>
);
